import { useState } from 'owa-react';
import React from 'react';
import { KeytipData } from '@fluentui/react/lib/KeytipData';
import {
    makeStyles,
    tokens,
    Button,
    Menu,
    MenuTrigger,
    MenuPopover,
    mergeClasses,
    type MenuOpenChangeData,
    type MenuOpenEvent,
} from '@fluentui/react-components';
import { observer } from 'owa-mobx-react';
import fileButtonStrings from './fileButton.locstring.json';
import loc from 'owa-localize';

interface FileButtonProps {
    menuContent: React.ReactNode;
    label?: string;
    onClick?: () => void;
}

const btnExecute = (executeTarget: HTMLElement | null, _target: HTMLElement | null) => {
    executeTarget?.click();
};

const keytipProps = {
    keySequences: ['f'],
    content: 'F',
    onExecute: btnExecute,
    hasMenu: true,
    hasDynamicChildren: true,
};
export const FileButton: React.FunctionComponent<FileButtonProps> = observer(
    ({ menuContent, label, onClick }) => {
        const styles = useStyles();
        const fileLabel = label || loc(fileButtonStrings.FileButton);
        const [open, setOpen] = useState(false);
        const buttonRef = React.useRef<HTMLButtonElement>(null);

        const handleOpenChange = React.useCallback(
            (event: MenuOpenEvent, data: MenuOpenChangeData) => {
                // If we're closing the menu, we want to stop propagation to prevent the event from bubbling to the compose handler
                // which would close the compose window if it was open.
                if (open && !data.open) {
                    event.stopPropagation();
                }
                setOpen(data.open);
            },
            [open]
        );

        const buttonElementFn = (props: {}) => (
            <MenuTrigger>
                <Button
                    appearance="transparent"
                    className={mergeClasses(styles.button, open && styles.selected)}
                    ref={buttonRef}
                    onClick={onClick}
                    {...props}
                >
                    {fileLabel}
                </Button>
            </MenuTrigger>
        );

        return (
            <Menu open={open} onOpenChange={handleOpenChange}>
                <KeytipData keytipProps={keytipProps}>{buttonElementFn}</KeytipData>

                <MenuPopover>{menuContent}</MenuPopover>
            </Menu>
        );
    },
    'FileButton'
);

const useStyles = makeStyles({
    button: {
        fontWeight: tokens.fontWeightRegular,
        height: '32px',
        marginLeft: '3px',
        marginRight: '2px',
        minWidth: 'auto',
        padding: `0 ${tokens.spacingHorizontalMNudge}`,
        ':hover': {
            backgroundColor: tokens.colorNeutralBackground2Hover,
            color: tokens.colorNeutralForeground1,
        },
    },
    selected: {
        backgroundColor: tokens.colorNeutralBackground1Pressed,
    },
});
